import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useROICalculator = () => {
  const [inputs, setInputs] = useState({
    callsPerWeek: "15",
    avgInterventionValue: "500",
    missedCallsPercent: "30",
    recoveredCallsPercent: "80",
    solutionPrice: "350",
  });
  const [results, setResults] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isLoadingPrice, setIsLoadingPrice] = useState(true);

  useEffect(() => {
    const fetchSolutionPrice = async () => {
      try {
        const response = await axios.get(`${API}/settings/`);
        if (response.data.solution_price) {
          setInputs((prev) => ({
            ...prev,
            solutionPrice: String(response.data.solution_price),
          }));
        }
      } catch {
        // keep default
      } finally {
        setIsLoadingPrice(false);
      }
    };
    fetchSolutionPrice();
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
    setHasCalculated(false);
  }, []);

  const getNumericValue = useCallback(
    (field) => {
      const val = parseFloat(inputs[field]);
      return isNaN(val) ? 0 : Math.max(0, val);
    },
    [inputs]
  );

  const calculateROI = useCallback(() => {
    const callsPerWeek = getNumericValue("callsPerWeek");
    const avgInterventionValue = getNumericValue("avgInterventionValue");
    const missedCallsPercent = getNumericValue("missedCallsPercent");
    const recoveredCallsPercent = getNumericValue("recoveredCallsPercent");
    const solutionPrice = getNumericValue("solutionPrice");

    const missedCallsPerWeekCalc = callsPerWeek * (missedCallsPercent / 100);
    const lostUrgenciesPerMonth = missedCallsPerWeekCalc * 4;
    const lostRevenue = lostUrgenciesPerMonth * avgInterventionValue;
    const recoveredUrgencies = lostUrgenciesPerMonth * (recoveredCallsPercent / 100);
    const recoveredRevenue = recoveredUrgencies * avgInterventionValue;
    const monthlyROI = recoveredRevenue - solutionPrice;
    const yearlyROI = monthlyROI * 12;
    const roiRatio =
      solutionPrice > 0
        ? ((recoveredRevenue - solutionPrice) / solutionPrice) * 100
        : 0;

    setResults({
      missedCallsPerWeek: missedCallsPerWeekCalc.toFixed(1),
      lostUrgenciesPerMonth: Math.round(lostUrgenciesPerMonth),
      lostRevenue: Math.round(lostRevenue),
      recoveredUrgencies: Math.round(recoveredUrgencies),
      recoveredRevenue: Math.round(recoveredRevenue),
      monthlyROI: Math.round(monthlyROI),
      yearlyROI: Math.round(yearlyROI),
      roiRatio: roiRatio.toFixed(0),
    });
    setHasCalculated(true);
  }, [getNumericValue]);

  useEffect(() => {
    if (!isLoadingPrice) calculateROI();
  }, [isLoadingPrice, calculateROI]);

  const getInterpretation = useCallback(() => {
    if (!results) return "";
    const { lostUrgenciesPerMonth: urgencies, recoveredUrgencies: recovered, recoveredRevenue: revenue } = results;
    if (urgencies === 0) return "Entrez vos donnees pour voir votre potentiel de recuperation.";
    return `D'apres vos estimations, votre entreprise pourrait perdre environ ${urgencies} urgences par mois a cause des appels manques. Avec un systeme qui securise chaque appel entrant, vous pourriez recuperer environ ${recovered} interventions supplementaires, ce qui represente environ ${revenue.toLocaleString("fr-CH")} CHF de chiffre d'affaires supplementaire chaque mois.`;
  }, [results]);

  return {
    inputs,
    results,
    hasCalculated,
    isLoadingPrice,
    handleInputChange,
    calculateROI,
    getInterpretation,
  };
};
